/**
 * CoverageMap — life coverage panel.
 *
 * Sprint 7 / Phase C: server now always returns the v2 derived shape
 * (`{ version: 2, overall, periods: [...] }`); the legacy
 * LifePeriod→pct branch and the version-detection fallback are gone.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  ChevronDown,
  CheckCircle2,
  Circle,
  CircleDot,
  CircleSlash,
  Loader2,
  MoreHorizontal,
  Sparkles,
} from 'lucide-react';
import { apiClient } from '@/services/api-client';
import {
  completeCard,
  reopenCard,
  reviveCard,
  type CoverageCard,
  type CoverageCardState,
  type CoverageV2Response,
} from '@/services/coverage-v2';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';
import { beatCoverageQueryKey, fetchBeatCoverage } from '@/services/story-beats';

/**
 * Beat-based coverage indexed by human cardId (e.g. "C001"). This is the
 * interviewer's notion of coverage (distinct Story Beats per card), shown
 * ALONGSIDE — never replacing — the coverage-v2 state label. Only fetched
 * under admin impersonation (a `targetUserId` is set); it's an observability
 * surface, not user-facing.
 */
interface BeatInfo {
  beats: number;
  pct: number;
}

function buildBeatCoverageMap(
  cards: Array<{ cardId: string; beats: number; coveragePct: number }> | undefined
): Map<string, BeatInfo> {
  const map = new Map<string, BeatInfo>();
  for (const c of cards ?? []) {
    map.set(c.cardId, { beats: c.beats, pct: c.coveragePct });
  }
  return map;
}

/**
 * Plain-language coverage redesign (Direction A) — ships dark behind a
 * build-time flag. Flip with `VITE_COVERAGE_PLAIN_LANGUAGE=true` (set in the
 * env / CI for the target deploy). When off, the panel renders exactly as
 * before (state label + "Story beats N/3" badge). When on, each row shows a
 * single progress bar + a plain-language status, collapsing the two competing
 * scales into one storyteller-friendly read.
 *
 * Read at CALL time (not module load) so tests can toggle it via
 * `vi.stubEnv('VITE_COVERAGE_PLAIN_LANGUAGE', 'true')`.
 */
function isPlainLanguageEnabled(): boolean {
  return import.meta.env.VITE_COVERAGE_PLAIN_LANGUAGE === 'true';
}

/** One row's derived plain-language coverage (bar fill + status text). */
interface PlainCoverage {
  /** Bar width, 0..100. */
  fillPct: number;
  /** Primary status line, e.g. "1 of 3 stories" or "Just started". */
  label: string;
  /** Secondary line, e.g. the cross-tag note. Optional. */
  sub?: string;
  /** Visual weight of the label. */
  tone: 'strong' | 'default' | 'muted';
  /** Fade the bar (cross-tagged beats — material from another topic). */
  faded?: boolean;
}

/**
 * Collapse the two coverage signals (the v2 engagement state and the
 * interviewer's beat count) into ONE plain-language read per card.
 *
 * - Skipped / completed are terminal and read directly.
 * - When beat data is present (admin impersonation) and the card has beats,
 *   we show the storyteller's "N of 3 stories". A card that was never
 *   directly engaged (`not_started`) yet carries beats got them by
 *   cross-tagging from another card — we surface that as a faded
 *   "N stories · told while on another topic" instead of a confusing
 *   "NOT YET + 1/3".
 * - Otherwise (user-facing view, or engaged-but-no-beats-yet) we fall back to
 *   the v2 state, mapped to a plain phrase and the card's own pct.
 */
function plainCoverageFor(
  card: CoverageCard,
  beatInfo: BeatInfo | undefined,
  beatTarget: number | null | undefined
): PlainCoverage {
  if (card.state === 'skipped') {
    return { fillPct: 0, label: 'Skipped', tone: 'muted' };
  }
  if (card.state === 'completed') {
    return {
      fillPct: 100,
      label: card.completionSource === 'user' ? 'You marked this done' : 'Story well captured',
      tone: 'strong',
    };
  }

  // Beat-aware path (admin observability under impersonation).
  if (beatInfo && beatTarget != null && beatInfo.beats > 0) {
    const noun = beatInfo.beats === 1 ? 'story' : 'stories';
    if (card.state === 'not_started') {
      // Cross-tag: the user never opened this card, but a beat told elsewhere
      // maps here. A feature, not a contradiction — show it gently.
      return {
        fillPct: beatInfo.pct,
        label: `${beatInfo.beats} ${noun}`,
        sub: 'told while on another topic',
        tone: 'muted',
        faded: true,
      };
    }
    return {
      fillPct: beatInfo.pct,
      label: `${beatInfo.beats} of ${beatTarget} stories`,
      tone: 'strong',
    };
  }

  // State-based fallback — drives the bar from the card's own pct.
  switch (card.state) {
    case 'rich':
      return { fillPct: card.pct, label: 'Well captured', tone: 'strong' };
    case 'going_deeper':
    case 'engaged':
      return { fillPct: card.pct, label: 'In progress', tone: 'strong' };
    case 'touched':
      // Touched but low pct — give a visible sliver so "just started" reads.
      return { fillPct: Math.max(card.pct, 12), label: 'Just started', tone: 'default' };
    case 'not_started':
    default:
      return { fillPct: 0, label: 'Not started', tone: 'muted' };
  }
}

/** Count root topics that have any engagement, for the plain-language header. */
function countRootTopics(roots: CardNode[]): { started: number; total: number } {
  let started = 0;
  for (const n of roots) if (subtreeHasEngagement(n)) started += 1;
  return { started, total: roots.length };
}

/**
 * Build the React Query key for coverage. Including targetUserId in the key
 * is load-bearing: when an admin switches between test users via the picker,
 * the sidebar MUST refetch rather than show the previous user's stale map.
 */
export function coverageQueryKey(targetUserId?: string | null) {
  return ['interviews', 'coverage', targetUserId ?? 'self'] as const;
}

export const COVERAGE_QUERY_KEY = coverageQueryKey(null);

export interface CoverageFetchError extends Error {
  status?: number;
}

export async function fetchCoverage(targetUserId?: string | null): Promise<CoverageV2Response> {
  const response = await apiClient.get<CoverageV2Response>(
    '/api/v1/interviews/coverage',
    targetUserId ? { targetUserId } : undefined
  );
  if (response.success) {
    return response.data as CoverageV2Response;
  }
  const err = new Error(response.error?.message ?? 'Failed to load coverage') as CoverageFetchError;
  const match = response.error?.message?.match(/status (\d+)/);
  if (match) err.status = Number(match[1]);
  throw err;
}

export function coverageRetry(failureCount: number, err: unknown): boolean {
  const status = (err as CoverageFetchError)?.status;
  if (status && status >= 400 && status < 500) return false;
  return failureCount < 2;
}

function isNotFound(error: unknown): boolean {
  return error instanceof Error && (error as CoverageFetchError).status === 404;
}

interface CoverageMapProps {
  className?: string;
  /** Admin "test as user" target. When set, fetches that user's map. */
  targetUserId?: string | null;
}

export function CoverageMap({ className, targetUserId }: CoverageMapProps) {
  const { data, isLoading, error } = useQuery<CoverageV2Response, CoverageFetchError>({
    queryKey: coverageQueryKey(targetUserId),
    queryFn: () => fetchCoverage(targetUserId),
    staleTime: 30_000,
    retry: coverageRetry,
  });

  // Beat-based coverage — admin observability only. Gated on `targetUserId`:
  // when an admin is "Acting as" a user, we surface the interviewer's beat
  // counts next to the v2 labels. Disabled (no fetch) in the user-facing view.
  const beatCoverageQuery = useQuery({
    queryKey: beatCoverageQueryKey(targetUserId ?? null),
    queryFn: () => fetchBeatCoverage(targetUserId!),
    enabled: !!targetUserId,
    staleTime: 30_000,
  });
  const beatCoverage = useMemo(
    () => buildBeatCoverageMap(beatCoverageQuery.data?.cards),
    [beatCoverageQuery.data]
  );
  const beatTarget = beatCoverageQuery.data?.target ?? null;

  if (isLoading) {
    return (
      <div
        className={cn('flex items-center justify-center rounded-lg border bg-card p-6', className)}
      >
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error && isNotFound(error)) {
    return (
      <div className={cn('rounded-lg border bg-card p-5', className)}>
        <h3 className="font-display text-base font-semibold text-foreground">Life Coverage</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          Coverage will populate after your first completed session.
        </p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className={cn('rounded-lg border bg-card p-5', className)}>
        <h3 className="font-display text-base font-semibold text-foreground">Life Coverage</h3>
        <p className="mt-2 text-sm text-destructive">Unable to load coverage right now.</p>
      </div>
    );
  }

  return (
    <CoverageMapV2View
      data={data}
      className={className}
      targetUserId={targetUserId ?? null}
      beatCoverage={beatCoverage}
      beatTarget={beatTarget}
    />
  );
}

// ─── v2 view ───────────────────────────────────────────────────────────────

interface CoverageMapV2ViewProps {
  data: CoverageV2Response;
  className?: string;
  targetUserId: string | null;
  beatCoverage: Map<string, BeatInfo>;
  beatTarget: number | null;
}

function CoverageMapV2View({
  data,
  className,
  targetUserId,
  beatCoverage,
  beatTarget,
}: CoverageMapV2ViewProps) {
  // Top-level rows are the **Group Root Story Cards** (depth-1, no parent),
  // pulled from each life period in canonical order. The life period itself
  // no longer renders as an accordion — it just dictates ordering. We build
  // the tree per-period so a card's parentCardId stays scoped to its period,
  // matching how the server assigns cards to periods.
  const rootNodes = useMemo(
    () => data.periods.flatMap((p) => buildCardTree(p.cards)),
    [data.periods]
  );

  // Overall % = mean of root cards' own pct (each root's pct already reflects
  // descendant engagement via the server's state-inheritance rubric). Distinct
  // from the API's `data.overall`, which averages period rollups — kept around
  // for mobile and other consumers but ignored here.
  const overall = useMemo(() => {
    if (rootNodes.length === 0) return 0;
    const sum = rootNodes.reduce((acc, n) => acc + n.card.pct, 0);
    return Math.round(sum / rootNodes.length);
  }, [rootNodes]);

  const plainLanguage = isPlainLanguageEnabled();
  const topics = useMemo(() => countRootTopics(rootNodes), [rootNodes]);

  return (
    <div className={cn('rounded-lg border bg-card p-5', className)}>
      <div className="flex items-baseline justify-between">
        <h3 className="font-display text-base font-semibold text-foreground">Life Coverage</h3>
        <span
          className="text-sm tabular-nums text-foreground"
          aria-label={`Overall coverage ${overall}%`}
        >
          {overall}%
        </span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {plainLanguage
          ? topics.started > 0
            ? `You've started ${topics.started} of ${topics.total} topics — keep going.`
            : 'Pick a topic to begin telling your story.'
          : 'Your story so far, told in your own words.'}
      </p>
      {rootNodes.length === 0 ? (
        <p className="mt-4 text-sm text-muted-foreground">
          Coverage will populate after your first completed session.
        </p>
      ) : (
        <ul className="mt-4 space-y-1" aria-label="Story cards">
          {rootNodes.map((node) => (
            <CardTreeNode
              key={node.card.cardId}
              node={node}
              targetUserId={targetUserId}
              indentLevel={0}
              beatCoverage={beatCoverage}
              beatTarget={beatTarget}
            />
          ))}
        </ul>
      )}
    </div>
  );
}

// ─── Tree construction ────────────────────────────────────────────────────

interface CardNode {
  card: CoverageCard;
  children: CardNode[];
}

/**
 * Build a parent → child forest from the flat card list within a period.
 * Cards whose `parentCardId` is not present in the same period (parent
 * inactive or hidden by user override) are promoted to roots so they're
 * never silently dropped.
 */
function buildCardTree(cards: CoverageCard[]): CardNode[] {
  const byCardId = new Map<string, CardNode>(
    cards.map((c) => [c.cardId, { card: c, children: [] }])
  );
  const roots: CardNode[] = [];
  for (const c of cards) {
    const node = byCardId.get(c.cardId)!;
    const parent = c.parentCardId ? byCardId.get(c.parentCardId) : undefined;
    if (parent) parent.children.push(node);
    else roots.push(node);
  }
  return roots;
}

function subtreeHasEngagement(node: CardNode): boolean {
  if (node.card.state !== 'not_started' && node.card.state !== 'skipped') return true;
  return node.children.some(subtreeHasEngagement);
}

// Tailwind-safe static class map so the purger keeps these classes. Indent
// step matches the period's own `pl-5` — every nested level adds one step.
const INDENT_CLASS: Record<number, string> = {
  0: '',
  1: 'pl-5',
  2: 'pl-10',
};

interface CardTreeNodeProps {
  node: CardNode;
  targetUserId: string | null;
  indentLevel: number;
  beatCoverage: Map<string, BeatInfo>;
  beatTarget: number | null;
}

function CardTreeNode({
  node,
  targetUserId,
  indentLevel,
  beatCoverage,
  beatTarget,
}: CardTreeNodeProps) {
  const hasChildren = node.children.length > 0;
  // Auto-open when any descendant is engaged so the engaged child is visible
  // on first render. Otherwise default to collapsed (per spec: children hidden
  // until the user expands the parent).
  const initiallyOpen = useMemo(
    () => hasChildren && node.children.some(subtreeHasEngagement),
    [hasChildren, node.children]
  );
  const [open, setOpen] = useState(initiallyOpen);

  const onToggle = hasChildren ? () => setOpen((v) => !v) : undefined;
  const childrenId = `card-${node.card.cardId}-children`;

  return (
    <li>
      <CardRow
        card={node.card}
        targetUserId={targetUserId}
        hasChildren={hasChildren}
        isOpen={open}
        onToggle={onToggle}
        childrenContainerId={childrenId}
        beatInfo={beatCoverage.get(node.card.cardId)}
        beatTarget={beatTarget}
      />
      {hasChildren && open && (
        <ul
          id={childrenId}
          className={cn('mt-1 space-y-1', INDENT_CLASS[Math.min(indentLevel + 1, 2)])}
          aria-label={`${node.card.title} sub-topics`}
        >
          {node.children.map((child) => (
            <CardTreeNode
              key={child.card.cardId}
              node={child}
              targetUserId={targetUserId}
              indentLevel={indentLevel + 1}
              beatCoverage={beatCoverage}
              beatTarget={beatTarget}
            />
          ))}
        </ul>
      )}
    </li>
  );
}

// ─── Card row ──────────────────────────────────────────────────────────────

interface CardRowProps {
  card: CoverageCard;
  targetUserId: string | null;
  /** True when this card has child cards under it (renders a chevron toggle). */
  hasChildren?: boolean;
  /** Whether the child list is currently expanded; ignored if !hasChildren. */
  isOpen?: boolean;
  /** Click handler for the chevron; required when hasChildren. */
  onToggle?: () => void;
  /** DOM id of the child <ul>, used for aria-controls. */
  childrenContainerId?: string;
  /** Beat-based coverage for this card (admin observability), or undefined. */
  beatInfo?: BeatInfo;
  /** Beat target (the N in `N/3`); null when beat coverage isn't loaded. */
  beatTarget?: number | null;
}

function CardRow({
  card,
  targetUserId,
  hasChildren = false,
  isOpen = false,
  onToggle,
  childrenContainerId,
  beatInfo,
  beatTarget,
}: CardRowProps) {
  const queryClient = useQueryClient();

  const onSettled = () => {
    void queryClient.invalidateQueries({ queryKey: coverageQueryKey(targetUserId) });
  };

  const completeMut = useMutation({ mutationFn: () => completeCard(card.cardId), onSettled });
  const reopenMut = useMutation({ mutationFn: () => reopenCard(card.cardId), onSettled });
  const reviveMut = useMutation({ mutationFn: () => reviveCard(card.cardId), onSettled });
  const isPending = completeMut.isPending || reopenMut.isPending || reviveMut.isPending;

  const Glyph = glyphFor(card.state);
  const stateLabel = stateLabelFor(card.state, card.completionSource);
  const plainLanguage = isPlainLanguageEnabled();

  return (
    <div className={cn('text-xs', card.state === 'skipped' && 'opacity-60')}>
      {/* Top line: chevron + glyph + card title + actions. In plain-language
          mode the progress bar/status move to their OWN line below (see the
          sub-line further down) — otherwise, on the narrow sidebar, the
          fixed-width bar+status starve the flex-1 title down to zero width
          (truncate sets overflow:hidden, which lets the flex item collapse to
          0) and the card NAME disappears. Keeping the title alone (with the
          actions menu) on this line guarantees it's always readable. */}
      <div className="flex items-center gap-2">
        {hasChildren ? (
          <button
            type="button"
            onClick={onToggle}
            aria-expanded={isOpen}
            aria-controls={childrenContainerId}
            aria-label={`${isOpen ? 'Collapse' : 'Expand'} ${card.title} sub-topics`}
            className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <ChevronDown
              className={cn('h-3 w-3 transition-transform', isOpen ? 'rotate-0' : '-rotate-90')}
              aria-hidden="true"
            />
          </button>
        ) : (
          // Reserve the chevron column so titles align across rows with/without children.
          <span className="inline-block h-3 w-3 shrink-0" aria-hidden="true" />
        )}
        <Glyph
          className={cn('h-3.5 w-3.5 shrink-0', glyphColorFor(card.state))}
          aria-hidden="true"
        />
        <span
          className={cn(
            'min-w-0 flex-1 truncate font-display text-foreground',
            card.state === 'skipped' && 'line-through'
          )}
          title={card.followUp ?? undefined}
        >
          {card.title}
        </span>
        {!plainLanguage && (
          <>
            <span className="text-[10px] uppercase tracking-wide text-muted-foreground">
              {stateLabel}
            </span>
            {beatInfo && beatTarget != null && (
              <span
                className="shrink-0 whitespace-nowrap rounded bg-orb-teal/10 px-1.5 py-0.5 text-[10px] font-medium tabular-nums text-orb-teal"
                title={`Story beats — the interviewer's beat-based coverage (distinct beats captured): ${beatInfo.beats} of ${beatTarget} (${beatInfo.pct}%)`}
              >
                Story beats {beatInfo.beats}/{beatTarget} · {beatInfo.pct}%
              </span>
            )}
          </>
        )}
        <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label={`More actions for ${card.title}`}
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            disabled={isPending}
          >
            {isPending ? (
              <Loader2 className="h-3 w-3 animate-spin" />
            ) : (
              <MoreHorizontal className="h-3 w-3" />
            )}
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="text-xs">
          {card.state !== 'completed' && card.state !== 'skipped' && (
            <DropdownMenuItem onClick={() => completeMut.mutate()}>Mark complete</DropdownMenuItem>
          )}
          {card.state === 'completed' && (
            <DropdownMenuItem onClick={() => reopenMut.mutate()}>Reopen</DropdownMenuItem>
          )}
          {card.state === 'skipped' && (
            <DropdownMenuItem onClick={() => reviveMut.mutate()}>Bring back</DropdownMenuItem>
          )}
          {card.lastTouchedAt && (
            <DropdownMenuItem asChild>
              <Link
                to={buildArchiveHref(targetUserId)}
                className="flex items-center gap-1"
              >
                View memories
                <ArrowRight className="h-3 w-3" />
              </Link>
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
        </DropdownMenu>
      </div>
      {plainLanguage && (
        // Progress on its own line, indented under the title (chevron+glyph
        // width). Full row width here, so the bar + status never compete with
        // the title for space.
        <div className="mt-0.5 flex items-center gap-2 pl-[34px]">
          <PlainCoverageIndicator plain={plainCoverageFor(card, beatInfo, beatTarget)} />
        </div>
      )}
    </div>
  );
}

/**
 * Plain-language trailing indicator: a single progress bar + a one- or
 * two-line status. Replaces the state-label + "Story beats N/3" badge when
 * the redesign flag is on. The leading glyph (state-driven) is unchanged.
 */
function PlainCoverageIndicator({ plain }: { plain: PlainCoverage }) {
  const aria = plain.sub ? `${plain.label} — ${plain.sub}` : plain.label;
  return (
    <span className="flex shrink-0 items-center gap-2" title={aria}>
      <span
        className="h-1.5 w-16 overflow-hidden rounded-full bg-muted sm:w-20"
        role="img"
        aria-label={aria}
      >
        <span
          className={cn('block h-full rounded-full bg-orb-gold', plain.faded && 'opacity-50')}
          style={{ width: `${plain.fillPct}%` }}
        />
      </span>
      <span
        className={cn(
          'min-w-[7.5rem] whitespace-nowrap text-[11px] leading-tight',
          plain.tone === 'strong' ? 'font-medium text-foreground' : 'text-muted-foreground'
        )}
      >
        {plain.label}
        {plain.sub && (
          <span className="block text-[10px] text-muted-foreground/80">{plain.sub}</span>
        )}
      </span>
    </span>
  );
}

// ─── State → visual mapping ────────────────────────────────────────────────

function glyphFor(state: CoverageCardState): typeof Circle {
  switch (state) {
    case 'completed':
      return CheckCircle2;
    case 'rich':
      return Sparkles;
    case 'going_deeper':
    case 'engaged':
      return CircleDot;
    case 'skipped':
      return CircleSlash;
    case 'touched':
    case 'not_started':
    default:
      return Circle;
  }
}

function glyphColorFor(state: CoverageCardState): string {
  switch (state) {
    case 'completed':
    case 'rich':
    case 'going_deeper':
    case 'engaged':
      return 'text-orb-gold';
    case 'touched':
      return 'text-orb-gold/60';
    case 'skipped':
      return 'text-muted-foreground';
    case 'not_started':
    default:
      return 'text-muted-foreground';
  }
}

function stateLabelFor(
  state: CoverageCardState,
  completionSource: 'user' | 'system' | null
): string {
  switch (state) {
    case 'completed':
      return completionSource === 'user' ? 'you marked done' : 'story is rich';
    case 'rich':
      return 'rich';
    case 'going_deeper':
      return 'going deeper';
    case 'engaged':
      return 'in progress';
    case 'touched':
      return 'we’ve started';
    case 'skipped':
      return 'skipped';
    case 'not_started':
    default:
      return 'not yet';
  }
}

function buildArchiveHref(targetUserId: string | null): string {
  const params = new URLSearchParams();
  params.set('tab', 'memories');
  if (targetUserId) params.set('asUser', targetUserId);
  return `/dashboard/ai-playground/memory-archive?${params.toString()}`;
}
